import type { Transaction } from "@/apis/gql/types";
import { useTable } from "@/components/shared/data-table/useTable";
import { type RawFilters, useServerSideQuery } from "@/hooks/useQueryFilters";
import { useTablePagination } from "@/hooks/useTablePagination";
import { useGetAllJournalEntries } from "@/queries/ledger/get-all";
import type { ExpandedState, Row } from "@tanstack/react-table";

import { getExpandedRowModel } from "@/lib/table/row-expanding";
import { useMemo, useState } from "react";
import { columns, mapRowToLedgerRow } from "./columns";

export type FilterFields = "documentType" | "documentReference";

export type LedgerEntriesTableInstance = ReturnType<
  typeof useLedgerEntriesTable
>;

export function useLedgerEntriesTable({
  tableName,
  queryKeyPrefix,
  storageKey,
  defaultFilters = {},
}: {
  tableName: string;
  queryKeyPrefix: string;
  storageKey?: string;
  defaultFilters?: RawFilters<FilterFields>;
}) {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const { page, setPage, limit, setLimit } = useTablePagination();

  const {
    globalSearch,
    setGlobalSearch,
    serverSideFilters,
    setFilters,
    filters,
    sorting,
    onSortingChange,
    sort,
    order,
    resetFilters,
    initialServerSideFilters,
  } = useServerSideQuery<FilterFields>({
    storageKey,
    defaultFilters,
    queryKeyPrefix,
    defaultSorting: { sort: "createdAt", order: "desc" },
  });

  const { data, loading, error, pagination } = useGetAllJournalEntries({
    query: serverSideFilters,
    global: globalSearch,
    page,
    limit,
    offset: (page - 1) * limit,
    sort,
    order,
    fetchPolicy: "network-only",
  });

  const _data = useMemo(() => {
    return mapRowToLedgerRow(data ?? []);
  }, [data]);

  const table = useTable({
    data: _data ?? [],
    columns,
    tableName,
    expanded,
    onExpandedChange: setExpanded,
    getRowId: (row) => row.id,
    getExpandedRowModel,
    getSubRows: (row) => row.subRows ?? [],
    sorting,
    onSortingChange,
    manualFiltering: true,
    manualSorting: true,
    manualPagination: true,
    serverSideFilters,
    onServerSideFiltersClear: resetFilters,
    initialServerSideFilters,
    globalServerSideSearch: globalSearch,
  });

  const handleRowClick = (
    event: React.MouseEvent,
    row: Row<Transaction>,
    index: number,
  ) => {
    table.setRowSelection((prev: Record<string, boolean>) => {
      const isSelected = !!prev[row.id];
      const selectedCount = Object.values(prev).filter(Boolean).length;

      return { [row.id]: !isSelected || selectedCount !== 1 };
    });
  };

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const handlePageSizeChange = (pageSize: number) => {
    setLimit(pageSize);
  };

  return {
    ...table,
    loading,
    error,
    pagination,
    handleRowClick,
    handlePageChange,
    handlePageSizeChange,
    expanded,
    setExpanded,
    globalSearch,
    setGlobalSearch,
    filters,
    setFilters,
    limit,
  };
}
